"use client";

import { type KeyboardEvent, useMemo, useState } from "react";
import { Form } from "react-aria-components";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { useRouter } from "next/navigation";

import {
  Header,
  InputComboBox,
  ListBoxItem,
  ListBoxSection,
} from "@jetblue/kit/input-combobox";
import { PairedButton } from "@jetblue/kit/paired-button";

export type InputSearchProperties<T extends string = string> = {
  labels: {
    heading: string;
    placeholder: string;
  };
  primaryLink: {
    href: string;
    isExternal: boolean;
    label: string;
    target: "_self" | "_blank";
  };
  quickLinks: {
    label: string;
    links?:
      | ({
          "@nodes": T[];
        } & Record<
          T,
          {
            href: string;
            isExternal: boolean;
            label: string;
            target?: "_self" | "_blank" | undefined;
          }
        >)
      | undefined;
  };
  theme?: "light" | "dark";
};

const appendSearchQueryString = ({
  href,
  search,
}: {
  href: string;
  search: string;
}): string => {
  const url =
    !href.startsWith("http://") && !href.startsWith("https://")
      ? new URL(href, "https://www.jetblue.com")
      : new URL(href);

  url.searchParams.set("search", search.trim());

  return url.href;
};

export const InputSearch = ({
  className,
  labels,
  primaryLink,
  quickLinks,
  theme,
}: InputSearchProperties & {
  className?: string | undefined;
}) => {
  const router = useRouter();
  const [inputValue, setInputValue] = useState("");

  const twClassname = twMerge(
    "flex w-full justify-center",
    clsx({ "bg-core-blue text-white": theme === "dark" }),
    className,
  );

  const links = useMemo(
    () =>
      quickLinks.links?.["@nodes"]?.map((node) => quickLinks.links?.[node]) ??
      [],
    [quickLinks.links],
  );

  const quickLinkNodes = useMemo(
    () =>
      links.map((link) => (
        <ListBoxItem
          className="flex min-h-8 !cursor-pointer items-center px-6 text-royal-blue hover:bg-royal-blue hover:text-white data-[focused]:bg-royal-blue data-[focused]:text-white"
          href={link?.href}
          id={link?.href}
          key={link?.href}
          textValue={link?.label}
        >
          {link?.label}
        </ListBoxItem>
      )),
    [links],
  );

  const searchHref = appendSearchQueryString({
    href: primaryLink.href,
    search: inputValue,
  });

  return (
    <Form
      className={twClassname}
      onSubmit={(event) => {
        event.preventDefault();
        router.push(searchHref);
      }}
    >
      <InputComboBox
        allowsCustomValue
        inputProperties={
          theme === "dark"
            ? {
                className: "focus-within:border-orange",
                inputClassName: "focus:!border-orange",
              }
            : undefined
        }
        inputValue={inputValue}
        label={labels.placeholder}
        onInputChange={setInputValue}
        onKeyUp={(event: KeyboardEvent) => {
          const ariaActiveDescendant =
            event.target instanceof Element &&
            event.target.getAttribute("aria-activedescendant");

          if (!ariaActiveDescendant && event.key === "Enter" && inputValue) {
            router.push(searchHref);
          }
        }}
      >
        {quickLinkNodes.length > 0 && (
          <ListBoxSection>
            <Header>{quickLinks.label}</Header>

            {quickLinkNodes}
          </ListBoxSection>
        )}
      </InputComboBox>

      <PairedButton
        aria-label={primaryLink.label}
        type="submit"
        variant={theme === "light" ? "primary" : "secondary"}
      />
    </Form>
  );
};
